import { Link } from 'react-router-dom';
import { diseases } from '@/mocks/home';
import { fetchDiseaseList } from '@/services/disease';
import { adaptDiseases } from '@/services/adapters';
import { useRequest } from '@/hooks/useRequest';
import SectionHeading from '@/components/base/SectionHeading';
import type { DiseaseItem } from '@/types/content';

const HOME_LIMIT = 6;

export default function DiseasesSection() {
  // 接口失败时降级为 mock 数据
  const { data: diseaseList } = useRequest<DiseaseItem[]>(
    () => fetchDiseaseList({ pageIndex: 1, pageSize: 999 }).then((res) => adaptDiseases(res.list)),
    { fallback: diseases },
  );

  // 接口已按后台「排序」升序返回，直接取前 N 个，保证与后台列表顺序一致
  const featuredDiseases = diseaseList.slice(0, HOME_LIMIT);

  // 无数据时整段隐藏，避免出现只有标题与按钮的空板块
  if (featuredDiseases.length === 0) return null;

  return (
    <section id="diseases" className="scroll-mt-24 w-full px-4 md:px-8 py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          eyebrow="优势病种"
          title="中西医结合优势病种"
          description="聚焦国家重点推广的优势病种中西医结合诊疗方案，以规范化诊疗方案与图文详情，帮助医师与患者系统了解辨证思路与调理路径。"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {featuredDiseases.map((item) => {
            const planCount = item.hospitalCount ?? 0;
            return (
              <Link
                key={item.id}
                to={`/diseases/${item.id}`}
                className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="relative w-full h-[190px] overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.name}
                    title={`${item.name} - 优势病种`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-background-50/90 backdrop-blur text-xs font-medium text-accent-700">
                    优势病种
                  </span>
                  {planCount > 0 && (
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-500/95 backdrop-blur text-xs font-medium text-background-50">
                      <i className="ri-hospital-line"></i>
                      {planCount} 家医院方案
                    </span>
                  )}
                </div>
                <div className="flex flex-col flex-1 p-5">
                  <h3 className="font-heading font-semibold text-lg text-foreground-900 text-balance group-hover:text-primary-600 transition-colors">
                    {item.name}
                  </h3>
                  <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    {item.symptom}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600">
                    查看图文详情
                    <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5"></i>
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/diseases"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            查看全部优势病种
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}